/**
 * DiceBear Style Registry
 *
 * DiceBear 10 dropped `@dicebear/collection`: styles now ship as standalone JSON
 * definitions in `@dicebear/styles`, with no namespace export to enumerate. This
 * registry is that missing index — a kebab-case key per style plus a lazy loader,
 * so the PWA bundle only pays for the definitions it actually renders (the full
 * set is ~2.2MB of raw JSON).
 *
 * The loaded `Style` instances live in a shallow-reactive map, so Vue computeds
 * that read `getStyle()` re-run once a definition finishes loading.
 */

import { shallowReactive } from 'vue';
import { Style } from '@dicebear/core';

/**
 * Lazy loaders keyed by style. Import specifiers must stay static string
 * literals for Vite to code-split them.
 */
const styleLoaders = {
  adventurer: () => import('@dicebear/styles/adventurer.json'),
  'adventurer-neutral': () => import('@dicebear/styles/adventurer-neutral.json'),
  avataaars: () => import('@dicebear/styles/avataaars.json'),
  'avataaars-neutral': () => import('@dicebear/styles/avataaars-neutral.json'),
  'big-ears': () => import('@dicebear/styles/big-ears.json'),
  'big-ears-neutral': () => import('@dicebear/styles/big-ears-neutral.json'),
  'big-smile': () => import('@dicebear/styles/big-smile.json'),
  bottts: () => import('@dicebear/styles/bottts.json'),
  'bottts-neutral': () => import('@dicebear/styles/bottts-neutral.json'),
  croodles: () => import('@dicebear/styles/croodles.json'),
  'croodles-neutral': () => import('@dicebear/styles/croodles-neutral.json'),
  disco: () => import('@dicebear/styles/disco.json'),
  dylan: () => import('@dicebear/styles/dylan.json'),
  'fun-emoji': () => import('@dicebear/styles/fun-emoji.json'),
  glass: () => import('@dicebear/styles/glass.json'),
  glyphs: () => import('@dicebear/styles/glyphs.json'),
  icons: () => import('@dicebear/styles/icons.json'),
  identicon: () => import('@dicebear/styles/identicon.json'),
  'initial-face': () => import('@dicebear/styles/initial-face.json'),
  initials: () => import('@dicebear/styles/initials.json'),
  lorelei: () => import('@dicebear/styles/lorelei.json'),
  'lorelei-neutral': () => import('@dicebear/styles/lorelei-neutral.json'),
  micah: () => import('@dicebear/styles/micah.json'),
  miniavs: () => import('@dicebear/styles/miniavs.json'),
  notionists: () => import('@dicebear/styles/notionists.json'),
  'notionists-neutral': () => import('@dicebear/styles/notionists-neutral.json'),
  'open-peeps': () => import('@dicebear/styles/open-peeps.json'),
  personas: () => import('@dicebear/styles/personas.json'),
  'pixel-art': () => import('@dicebear/styles/pixel-art.json'),
  'pixel-art-neutral': () => import('@dicebear/styles/pixel-art-neutral.json'),
  rings: () => import('@dicebear/styles/rings.json'),
  'shape-grid': () => import('@dicebear/styles/shape-grid.json'),
  shapes: () => import('@dicebear/styles/shapes.json'),
  stripes: () => import('@dicebear/styles/stripes.json'),
  thumbs: () => import('@dicebear/styles/thumbs.json'),
  'toon-head': () => import('@dicebear/styles/toon-head.json'),
  triangles: () => import('@dicebear/styles/triangles.json'),
} as const;

export type AvatarStyleKey = keyof typeof styleLoaders;

export const avatarStyleKeys = Object.keys(styleLoaders) as AvatarStyleKey[];

export const isAvatarStyleKey = (key: unknown): key is AvatarStyleKey =>
  typeof key === 'string' && key in styleLoaders;

/** Resolved styles, reactive so computeds refresh as definitions arrive. */
const loadedStyles = shallowReactive(new Map<AvatarStyleKey, Style>());

/** In-flight loads, so concurrent callers share one import per style. */
const pendingLoads = new Map<AvatarStyleKey, Promise<Style | undefined>>();

/**
 * Returns an already-loaded style, or undefined when it still needs loading.
 * Reading this inside a computed subscribes it to the style becoming available.
 */
export const getStyle = (key: AvatarStyleKey): Style | undefined =>
  loadedStyles.get(key);

/**
 * Loads (and caches) a single style definition.
 */
export const loadStyle = async (
  key: AvatarStyleKey,
): Promise<Style | undefined> => {
  const cached = loadedStyles.get(key);
  if (cached) return cached;

  const pending = pendingLoads.get(key);
  if (pending) return pending;

  const load = (async () => {
    try {
      const module = await styleLoaders[key]();
      // Vite exposes JSON as a default export; guard for raw-object interop.
      const definition = (module as { default?: unknown }).default ?? module;
      const style = new Style(definition);
      loadedStyles.set(key, style);
      return style;
    } catch (e) {
      console.error(`Failed to load DiceBear style "${key}":`, e);
      return undefined;
    } finally {
      pendingLoads.delete(key);
    }
  })();

  pendingLoads.set(key, load);
  return load;
};

/**
 * Loads every registered style. Used to fill the style-picker grid after the
 * selected style is already on screen.
 */
export const loadAllStyles = async (): Promise<void> => {
  await Promise.all(avatarStyleKeys.map((key) => loadStyle(key)));
};
